/**
 * @module tree/inserted
 */

/**
 * @typedef {Element|Text} Target
 */

import mutations from './mutations.js'
import aborting from '../utils/aborting.js'
import never from '../utils/never.js'

const options = {
  childList: true,
  subtree: true
}

/**
 * Awaits the target insertion into the provided container
 * @param {Target} target The node to be inserted
 * @param {Node} container The future container of the node
 * @param {?Promise} abort The abortion promise
 * @return {Promise<Target>}
 */
export default async (target, container, abort = never) => {
  if (container.contains(target)) {
    return target
  }
  
  const [aborted, symbol] = aborting(abort)
  const records = mutations(container, options)
  
  while (true) {
    const result = await Promise.race([aborted, records.next()])
    
    if (result === symbol) {
      return target
    }
    
    const { value } = result
    const { addedNodes } = value
    
    for (const node of addedNodes) {
      if (node === target || node?.contains(target)) {
        await records.return(null)
        
        return target
      }
    }
  }
}
